/**
 * Reads what the home's plan section shows (owner rulings 2026-10-16).
 *
 * The plan in force comes from plan_force_states (migration 919) and its ranks
 * from item_official_ranks — the Plan of Record, not the cycle report the old
 * home read (which Sightline never had, so it said "no agreed plan"). Around
 * it: who agreed (the cycle the plan came from: its published ranking and its
 * catchball turns), what changed since (audit_events, the list watchers are
 * told about), whose items are whose, what was delivered, any cycle making the
 * next plan that the viewer is in the flow of, and agreements left provisional.
 *
 * Every read is the viewer's own (row security applies). A part that cannot be
 * read is reported as unread, never as "none".
 */

import { useEffect, useState } from 'react';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import { fetchPlanForceStates, type PlanForceRow } from '../../lib/planForceService';
import { getAllDeliveriesForWorkspace } from '../../lib/itemDeliveryService';
import { useCycleProgressStore, type CycleProgress } from '../../stores/prioritization/cycleProgressStore';
import { isUuid } from '../../lib/isUuid';
import {
  ITEM_CHANGE_EVENTS,
  cycleSteps,
  cyclesBeingMade,
  pickPlanInForce,
  pickStartsLater,
  seesCycleBeingMade,
  sinceAgreed,
  type CycleLite,
  type CycleStep,
  type ItemPeople,
  type PlanRank,
  type ProposedRank,
  type ProvisionalAgreement,
  type SinceAgreed,
} from './lib/plan';

export interface PlanTrailFacts {
  session: { name: string; publishedAt: string | null; facilitatorId: string | null; participantCount: number } | null;
  turns: Array<{ submittedAt: string | null; submittedBy: string | null; resolvedAt: string | null; resolvedBy: string | null; decision: string | null }>;
}

export interface PlanInForceView {
  planCycleId: string;
  name: string;
  commitment: string | null;
  authorizedAt: string | null;
  /** A user id when the authorizer was recorded as one, else the recorded words ("admin"). */
  authorizedById: string | null;
  authorizedByText: string | null;
  version: number | null;
  /** The day a next authorized plan takes over, when one is waiting. */
  untilOn: string | null;
  ranks: PlanRank[];
  /** Null when the changes could not be read. */
  since: SinceAgreed | null;
  sourceCycle: { id: string; name: string } | null;
  trail: PlanTrailFacts;
}

export interface CycleInMaking {
  cycle: CycleLite;
  designed: boolean;
  steps: CycleStep[] | null;
  listCount: number;
  /** The latest proposed order (a catchball turn, else a priority list). */
  proposed: { rows: ProposedRank[]; cut: number | null } | null;
  /** A few items on the list, when nothing is ranked yet. */
  onTheList: ProposedRank[];
  dateRange: { start: string | null; end: string | null } | null;
  ballHolderId: string | null;
  ballSince: string | null;
}

export interface HomePlanData {
  loading: boolean;
  /** The read for a cycle making the next plan has not finished: "no plan" is not yet known. */
  makingLoading: boolean;
  /** Active items in the workspace, counted when there is no plan in force (null otherwise). */
  itemsOnList: number | null;
  /** The plan's state could not be read at all. */
  unread: boolean;
  inForce: PlanInForceView | null;
  making: CycleInMaking | null;
  alsoMaking: Array<{ id: string; name: string }>;
  provisional: ProvisionalAgreement[];
  itemPeople: Record<string, ItemPeople>;
  delivered: ReadonlySet<string>;
}

export interface HomePlanInput {
  workspaceId: string | null;
  viewerId: string | null;
  isAdmin: boolean;
  cycles: CycleLite[];
  cycleMeta: Record<string, Record<string, unknown> | undefined>;
  loops: Array<{
    id: string;
    cycleId: string;
    state: string;
    closedAt?: Date | null;
    ballHolderUserId?: string | null;
    ballClaimedAt?: string | null;
    proposalSubmittedAt?: Date | null;
  }>;
  /** Changes on every visit (the location key), so the home re-reads. */
  refreshKey: string;
}

const MAX_CANDIDATES = 5;
const ON_THE_LIST = 4;

async function readSourceCycles(states: PlanForceRow[]): Promise<{
  names: Map<string, { name: string; commitment: string | null }>;
  sourceByPlan: Map<string, string>;
}> {
  const db = getTypedSupabaseClient();
  const ids = states.map((s) => s.planCycleId);
  const names = new Map<string, { name: string; commitment: string | null }>();
  const sourceByPlan = new Map<string, string>();
  if (ids.length === 0) return { names, sourceByPlan };
  const { data: plans } = await db
    .from('plan_cycles')
    .select('id, name, commitment_statement, input_type, input_reference_id')
    .in('id', ids);
  const poolRefs = new Map<string, string>();
  const setRefs = new Map<string, string>();
  for (const p of plans ?? []) {
    names.set(p.id, { name: p.name, commitment: p.commitment_statement ?? null });
    if (!p.input_reference_id) continue;
    if (p.input_type === 'item_pool') poolRefs.set(p.input_reference_id, p.id);
    else if (p.input_type === 'published_priority_list') setRefs.set(p.input_reference_id, p.id);
  }
  // A plan comes from a cycle through the pool or the priority list it was built from.
  const [{ data: pools }, { data: sets }] = await Promise.all([
    poolRefs.size ? db.from('item_pools').select('id, cycle_id').in('id', [...poolRefs.keys()]) : Promise.resolve({ data: [] as { id: string; cycle_id: string | null }[] }),
    setRefs.size ? db.from('priority_sets').select('id, cycle_id').in('id', [...setRefs.keys()]) : Promise.resolve({ data: [] as { id: string; cycle_id: string | null }[] }),
  ]);
  for (const p of pools ?? []) if (p.cycle_id) sourceByPlan.set(poolRefs.get(p.id)!, p.cycle_id);
  for (const s of sets ?? []) if (s.cycle_id) sourceByPlan.set(setRefs.get(s.id)!, s.cycle_id);
  return { names, sourceByPlan };
}

async function readPlanInForce(
  row: PlanForceRow,
  names: Map<string, { name: string; commitment: string | null }>,
  sourceCycleId: string | null,
  sourceCycleName: string | null,
  untilOn: string | null,
): Promise<PlanInForceView | null> {
  const db = getTypedSupabaseClient();
  const { data: rankRows, error } = await db
    .from('item_official_ranks')
    .select('item_id, item_title, rank, above_the_line, authorized_at, authorized_by, authorized_by_user_id, plan_version_id, plan_of_record_snapshots(superseded_by_snapshot_id)')
    .eq('plan_cycle_id', row.planCycleId);
  if (error) return null;
  const current = (rankRows ?? []).filter((r) => r.plan_of_record_snapshots?.superseded_by_snapshot_id == null);
  // The latest authorization's ranks; an older snapshot left unsuperseded never mixes in.
  const latestAt = current.reduce<string | null>((m, r) => (!m || (r.authorized_at ?? '') > m ? r.authorized_at : m), null);
  const ranks = current.filter((r) => r.authorized_at === latestAt);
  const first = ranks[0];

  const ids = ranks.map((r) => r.item_id);
  const [{ data: version }, changes, session, turns] = await Promise.all([
    first?.plan_version_id
      ? db.from('plan_versions').select('version_number').eq('id', first.plan_version_id).maybeSingle()
      : Promise.resolve({ data: null as { version_number: number } | null }),
    ids.length && latestAt
      ? db
          .from('audit_events')
          .select('item_id, created_by, created_at')
          .in('item_id', ids)
          .in('event_type', [...ITEM_CHANGE_EVENTS])
          .gte('created_at', latestAt)
          .limit(1000)
      : Promise.resolve({ data: [] as { item_id: string | null; created_by: string | null; created_at: string }[], error: null }),
    sourceCycleId
      ? db
          .from('prioritization_sessions')
          .select('name, published_at, facilitator_id, participant_ids')
          .eq('cycle_id', sourceCycleId)
          .eq('state', 'PUBLISHED')
          .order('published_at', { ascending: false })
          .limit(1)
          .maybeSingle()
      : Promise.resolve({ data: null }),
    sourceCycleId
      ? db
          .from('loop_results')
          .select('submitted_at, submitted_by, resolved_at, resolved_by, evaluator_decision')
          .eq('cycle_id', sourceCycleId)
          .eq('direction', 'up')
          .order('submitted_at', { ascending: true })
      : Promise.resolve({ data: [] }),
  ]);

  const doingIds = new Set(ranks.filter((r) => r.above_the_line).map((r) => r.item_id));
  const since = changes.error
    ? null
    : sinceAgreed(
        (changes.data ?? [])
          .filter((e): e is { item_id: string; created_by: string | null; created_at: string } => !!e.item_id)
          .map((e) => ({ itemId: e.item_id, createdBy: e.created_by, createdAt: e.created_at })),
        doingIds,
      );

  const by = first?.authorized_by_user_id ?? (isUuid(first?.authorized_by) ? first?.authorized_by ?? null : null);
  const s = session.data as { name: string; published_at: string | null; facilitator_id: string | null; participant_ids: string[] | null } | null;
  return {
    planCycleId: row.planCycleId,
    name: names.get(row.planCycleId)?.name ?? 'The plan',
    commitment: names.get(row.planCycleId)?.commitment ?? null,
    authorizedAt: latestAt ?? row.authorizedAt,
    authorizedById: by,
    authorizedByText: by ? null : first?.authorized_by ?? null,
    version: version?.version_number ?? null,
    untilOn,
    ranks: ranks
      .filter((r): r is typeof r & { rank: number } => r.rank != null)
      .map((r) => ({ itemId: r.item_id, title: r.item_title ?? 'Untitled item', rank: r.rank, aboveTheLine: r.above_the_line })),
    since,
    sourceCycle: sourceCycleId ? { id: sourceCycleId, name: sourceCycleName ?? 'its cycle' } : null,
    trail: {
      session: s ? { name: s.name, publishedAt: s.published_at, facilitatorId: s.facilitator_id, participantCount: s.participant_ids?.length ?? 0 } : null,
      turns: ((turns.data ?? []) as Array<{ submitted_at: string | null; submitted_by: string | null; resolved_at: string | null; resolved_by: string | null; evaluator_decision: string | null }>).map((t) => ({
        submittedAt: t.submitted_at,
        submittedBy: t.submitted_by,
        resolvedAt: t.resolved_at,
        resolvedBy: t.resolved_by,
        decision: t.evaluator_decision,
      })),
    },
  };
}

async function readViewerPlace(viewerId: string): Promise<string[][]> {
  const { data } = await getTypedSupabaseClient()
    .from('user_node_assignments')
    .select('org_unit_id, org_units(path)')
    .eq('user_id', viewerId)
    .eq('active', true);
  return (data ?? []).map((a) => {
    const path = ((a.org_units as { path: string[] | null } | null)?.path ?? []).map(String);
    return path.includes(a.org_unit_id) ? path : [...path, a.org_unit_id];
  });
}

async function readProposed(cycleId: string): Promise<{ ids: string[]; cut: number | null } | null> {
  const db = getTypedSupabaseClient();
  const { data: turn } = await db
    .from('loop_results')
    .select('aggregated_rank_list, aggregated_cut_line_position')
    .eq('cycle_id', cycleId)
    .not('aggregated_rank_list', 'is', null)
    .order('submitted_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  // A cut line at 0 is one not drawn yet, never "nothing above the line".
  const line = (n: number | null | undefined) => (n != null && n > 0 ? n : null);
  if (turn?.aggregated_rank_list?.length) return { ids: turn.aggregated_rank_list, cut: line(turn.aggregated_cut_line_position) };
  const { data: set } = await db
    .from('priority_sets')
    .select('ranked_item_ids, cut_line_position')
    .eq('cycle_id', cycleId)
    .neq('state', 'superseded')
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (set?.ranked_item_ids?.length) return { ids: set.ranked_item_ids, cut: line(set.cut_line_position) };
  return null;
}

async function readTitles(ids: string[]): Promise<Map<string, string>> {
  if (ids.length === 0) return new Map();
  const { data } = await getTypedSupabaseClient().from('items').select('id, title').in('id', ids);
  return new Map((data ?? []).map((i) => [i.id, i.title ?? 'Untitled item']));
}

async function readMaking(
  candidates: CycleLite[],
  input: HomePlanInput,
  fetchProgress: (cycleId: string) => Promise<CycleProgress | null>,
): Promise<{ making: CycleInMaking | null; alsoMaking: Array<{ id: string; name: string }> }> {
  const viewerId = input.viewerId!;
  const db = getTypedSupabaseClient();
  const ids = candidates.map((c) => c.id);
  if (ids.length === 0) return { making: null, alsoMaking: [] };
  const [{ data: tracks }, viewerPaths] = await Promise.all([
    db.from('tracks').select('id, cycle_id, org_unit_id, metadata').in('cycle_id', ids),
    readViewerPlace(viewerId),
  ]);
  const trackIds = (tracks ?? []).map((t) => t.id);
  const { data: seats } = trackIds.length
    ? await db.from('track_participants').select('track_id').eq('user_id', viewerId).is('removed_at', null).in('track_id', trackIds)
    : { data: [] as { track_id: string }[] };
  const seatTracks = new Set((seats ?? []).map((s) => s.track_id));

  // Who sees which cycle is decided from its tracks and loops alone; the
  // (expensive) progress view is read only for the one cycle the home shows.
  const visible = candidates
    .map((c) => {
      const own = (tracks ?? []).filter((t) => t.cycle_id === c.id);
      const loops = input.loops.filter((l) => l.cycleId === c.id);
      const designed = own.length > 0 || loops.length > 0;
      const sees = seesCycleBeingMade({
        viewerId,
        isAdmin: input.isAdmin,
        ownerId: c.ownerId,
        createdBy: c.createdBy,
        designed,
        onATrack: own.some((t) => seatTracks.has(t.id)),
        trackUnitIds: own.map((t) => t.org_unit_id).filter((u): u is string => !!u),
        viewerUnitPaths: viewerPaths,
      });
      return { cycle: c, tracks: own, loops, designed, sees };
    })
    .filter((x) => x.sees);

  const top = visible[0];
  if (!top) return { making: null, alsoMaking: [] };

  const { cycle, loops, designed } = top;
  const progress = designed ? await fetchProgress(cycle.id).catch(() => null) : null;
  const loop = [...loops].sort((a, b) => (b.ballClaimedAt ?? '').localeCompare(a.ballClaimedAt ?? ''))[0];
  const loopState = (loop?.state ?? progress?.loopState ?? '').toLowerCase();
  const hasPath = top.tracks.some((t) => {
    const path = (t.metadata as Record<string, unknown> | null)?.designerPathOrgUnitIds;
    return Array.isArray(path) && path.length > 1;
  });
  const rankingDone = (progress?.rankingSessionsComplete ?? 0) > 0 || (progress?.egapRankingSessionsComplete ?? 0) > 0;
  const steps = designed
    ? cycleSteps({
        participants: progress?.activeParticipantCount ?? 0,
        items: Math.max(progress?.confirmedItemCount ?? 0, progress?.poolItemCount ?? 0),
        criteriaLocked: !!(progress?.criteriaLocked || progress?.egapCriteriaLocked),
        valuesDone: !!progress?.egapValuesConverged,
        rankingDone,
        hasCatchball: loops.length > 0 || hasPath,
        agreed: loopState === 'accepted' || loopState === 'closed' ? 'yes' : loopState === 'accepted_provisional' ? 'provisional' : 'no',
        authorized: false,
      })
    : null;

  const proposedRaw = designed ? await readProposed(cycle.id) : null;
  let onTheListIds: string[] = [];
  if (!proposedRaw && designed) {
    const { data: pools } = await db.from('item_pools').select('priority_ids').eq('cycle_id', cycle.id).limit(5);
    onTheListIds = [...new Set((pools ?? []).flatMap((p) => p.priority_ids ?? []))].slice(0, ON_THE_LIST);
  }
  const titles = await readTitles([...(proposedRaw?.ids ?? []), ...onTheListIds]);
  const range = input.cycleMeta[cycle.id]?.designerDateRange as { start?: string | null; end?: string | null } | undefined;

  return {
    making: {
      cycle,
      designed,
      steps,
      listCount: Math.max(progress?.confirmedItemCount ?? 0, progress?.poolItemCount ?? 0),
      proposed: proposedRaw
        ? { rows: proposedRaw.ids.map((id) => ({ itemId: id, title: titles.get(id) ?? 'Untitled item' })), cut: proposedRaw.cut }
        : null,
      onTheList: onTheListIds.map((id) => ({ itemId: id, title: titles.get(id) ?? 'Untitled item' })),
      dateRange: range ? { start: range.start ?? null, end: range.end ?? null } : null,
      ballHolderId: loop?.ballHolderUserId ?? progress?.ballHolderUserId ?? null,
      ballSince: loop?.ballClaimedAt ?? null,
    },
    alsoMaking: visible.slice(1).map((v) => ({ id: v.cycle.id, name: v.cycle.name })),
  };
}

async function readProvisional(input: HomePlanInput): Promise<ProvisionalAgreement[]> {
  const loops = input.loops.filter((l) => l.state.toLowerCase() === 'accepted_provisional' && !l.closedAt);
  if (loops.length === 0) return [];
  const { data } = await getTypedSupabaseClient()
    .from('loop_results')
    .select('catchball_loop_id, resolved_at')
    .in('catchball_loop_id', loops.map((l) => l.id))
    .eq('evaluator_decision', 'accepted_provisional')
    .order('resolved_at', { ascending: false });
  const acceptedAt = new Map<string, string>();
  for (const r of data ?? []) if (r.resolved_at && !acceptedAt.has(r.catchball_loop_id)) acceptedAt.set(r.catchball_loop_id, r.resolved_at);
  const cycles = new Map(input.cycles.map((c) => [c.id, c]));
  return loops
    .map((l) => {
      const at = acceptedAt.get(l.id) ?? l.proposalSubmittedAt?.toISOString() ?? null;
      const cycle = cycles.get(l.cycleId);
      if (!at || !cycle) return null;
      return { loopId: l.id, cycleId: l.cycleId, cycleName: cycle.name, ownerId: cycle.ownerId, ballHolderId: l.ballHolderUserId ?? null, acceptedAt: at };
    })
    .filter((x): x is ProvisionalAgreement => x !== null);
}

async function readPeople(ids: string[]): Promise<Record<string, ItemPeople>> {
  if (ids.length === 0) return {};
  const { data } = await getTypedSupabaseClient().from('items').select('id, owner_id, delegate_id').in('id', ids);
  return Object.fromEntries((data ?? []).map((p) => [p.id, { ownerId: p.owner_id, delegateId: p.delegate_id }]));
}

interface PlanPart {
  loading: boolean;
  unread: boolean;
  states: PlanForceRow[] | null;
  sourceCycleIds: ReadonlySet<string>;
  inForce: PlanInForceView | null;
  delivered: ReadonlySet<string>;
  people: Record<string, ItemPeople>;
  itemsOnList: number | null;
}

const PLAN_START: PlanPart = { loading: true, unread: false, states: null, sourceCycleIds: new Set(), inForce: null, delivered: new Set(), people: {}, itemsOnList: null };

async function countItemsOnList(workspaceId: string): Promise<number | null> {
  const { count, error } = await getTypedSupabaseClient()
    .from('items')
    .select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId)
    .eq('exit_status', 'active');
  return error ? null : count ?? null;
}

/**
 * Three independent reads, so a slow one never holds up the others and the
 * cycles and loops arriving in the stores never restart the plan's read:
 *   1. the plan in force (per visit and workspace)
 *   2. the cycle making the next plan (when the cycles or loops change)
 *   3. agreements left provisional (when the loops change)
 */
export function useHomePlan(input: HomePlanInput): HomePlanData {
  const fetchProgress = useCycleProgressStore((s) => s.fetchProgress);
  const { workspaceId, viewerId, isAdmin, cycles, loops, refreshKey } = input;
  const cyclesKey = cycles.map((c) => `${c.id}:${c.status}`).join(',');
  const loopsKey = loops.map((l) => `${l.id}:${l.state}`).join(',');

  // 1 · The plan in force.
  const [plan, setPlan] = useState<PlanPart>(PLAN_START);
  useEffect(() => {
    if (!workspaceId || !viewerId) return;
    let cancelled = false;
    setPlan((p) => ({ ...p, loading: true }));
    void (async () => {
      try {
        const states = await fetchPlanForceStates(workspaceId);
        if (cancelled) return;
        if (states === null) {
          setPlan({ ...PLAN_START, loading: false, unread: true });
          return;
        }
        const inForceRow = pickPlanInForce(states);
        const { names, sourceByPlan } = await readSourceCycles(states.filter((s) => s.authorizedAt));
        const sourceId = inForceRow ? sourceByPlan.get(inForceRow.planCycleId) ?? null : null;
        const [inForce, deliveries, itemsOnList] = await Promise.all([
          inForceRow
            ? readPlanInForce(inForceRow, names, sourceId, null, pickStartsLater(states)?.takesEffectOn ?? null)
            : Promise.resolve(null),
          getAllDeliveriesForWorkspace(workspaceId).catch(() => []),
          inForceRow ? Promise.resolve(null) : countItemsOnList(workspaceId).catch(() => null),
        ]);
        const people = await readPeople(inForce?.ranks.map((r) => r.itemId) ?? []);
        // An item is delivered when its latest delivery record says shipped.
        const latest = new Map<string, string>();
        for (const d of deliveries) if (!latest.has(d.itemId)) latest.set(d.itemId, d.deliveryType);
        if (cancelled) return;
        setPlan({
          loading: false,
          unread: inForceRow !== null && inForce === null,
          states,
          sourceCycleIds: new Set(sourceByPlan.values()),
          inForce,
          delivered: new Set([...latest.entries()].filter(([, t]) => t === 'shipped').map(([id]) => id)),
          people,
          itemsOnList,
        });
      } catch {
        if (!cancelled) setPlan({ ...PLAN_START, loading: false, unread: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, viewerId, refreshKey]);

  // 2 · The cycle making the next plan, once the plan's state is known.
  const [making, setMaking] = useState<{
    loading: boolean;
    making: CycleInMaking | null;
    alsoMaking: Array<{ id: string; name: string }>;
    people: Record<string, ItemPeople>;
  }>({ loading: true, making: null, alsoMaking: [], people: {} });
  const inForceSince = plan.states ? pickPlanInForce(plan.states)?.effectiveOn ?? null : null;
  const statesReady = plan.states !== null;
  const sourceKey = [...plan.sourceCycleIds].join(',');
  useEffect(() => {
    if (!statesReady || !viewerId) return;
    let cancelled = false;
    void (async () => {
      try {
        const candidates = cyclesBeingMade(cycles, plan.sourceCycleIds, inForceSince).slice(0, MAX_CANDIDATES);
        const found = await readMaking(candidates, input, fetchProgress);
        const people = await readPeople(found.making?.proposed?.rows.map((r) => r.itemId) ?? []);
        if (!cancelled) setMaking({ loading: false, ...found, people });
      } catch {
        if (!cancelled) setMaking({ loading: false, making: null, alsoMaking: [], people: {} });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the stores' ids and states, not their array identities
  }, [statesReady, sourceKey, inForceSince, cyclesKey, loopsKey, viewerId, isAdmin, refreshKey, fetchProgress]);

  // 3 · Agreements left provisional.
  const [provisional, setProvisional] = useState<ProvisionalAgreement[]>([]);
  useEffect(() => {
    let cancelled = false;
    void readProvisional(input)
      .then((rows) => {
        if (!cancelled) setProvisional(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the loops' ids and states and the cycles they belong to
  }, [loopsKey, cyclesKey, refreshKey]);

  const sourceName = plan.inForce?.sourceCycle ? cycles.find((c) => c.id === plan.inForce!.sourceCycle!.id)?.name ?? null : null;
  const inForce = plan.inForce && plan.inForce.sourceCycle && sourceName
    ? { ...plan.inForce, sourceCycle: { ...plan.inForce.sourceCycle, name: sourceName } }
    : plan.inForce;

  return {
    loading: plan.loading,
    makingLoading: making.loading,
    itemsOnList: plan.itemsOnList,
    unread: plan.unread,
    inForce,
    making: making.making,
    alsoMaking: making.alsoMaking,
    provisional,
    itemPeople: { ...making.people, ...plan.people },
    delivered: plan.delivered,
  };
}
